import React from "react";
import styled from "styled-components";
import { Role } from "features/whop/whopLanding/WhopLandingPage.tsx";
import {
  SectionContainer,
  CalendarIcon,
} from "features/whop/whopLanding/components/shared.tsx";
import { fadeUpAnimation } from "features/whop/whopLanding/components/animations.tsx";
import { heroContent as content } from "features/whop/whopLanding/content.ts";

interface HeroProps {
  role: Role;
}

export const HeroSection: React.FC<HeroProps> = ({ role }) => {
  const text = content[role];
  return (
    <Section>
      <Inner>
        <AnimatedBlock $delay={0}>
          <TopGroup>
            <Headline>{text.headline}</Headline>
            <Subheadline>{text.subheadline}</Subheadline>
            <SupportText>{text.support}</SupportText>
          </TopGroup>
        </AnimatedBlock>
        {role === "admin" && (
          <AnimatedBlock $delay={120}>
            <CTAGroup>
              <SecondaryButton
                onClick={() =>
                  window.open(
                    "https://calendly.com/cornix-trading/cornix-channel-onboarding",
                    "_blank"
                  )
                }
              >
                <CalendarIcon />
                {text.secondaryCTA}
              </SecondaryButton>
            </CTAGroup>
          </AnimatedBlock>
        )}
        <AnimatedBlock $delay={240}>
          <VideoPlaceholder>
            <VideoBox>
              <iframe
                src={YOUTUBE_EMBED_LINK}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </VideoBox>
          </VideoPlaceholder>
        </AnimatedBlock>
      </Inner>
    </Section>
  );
};

const MessageIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const PlayIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polygon points="10 8 16 12 10 16 10 8" />
  </svg>
);

const YOUTUBE_EMBED_LINK = "https://www.youtube.com/embed/s-TGuPticPw?rel=0";

const Section = styled(SectionContainer)`
  position: relative;
  padding: 96px 24px 80px;
  max-width: 100%;
  background: ${(p) =>
    p.theme.isDarkMode
      ? "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(37,99,235,0.12) 0%, transparent 70%), #0a0a0a"
      : "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(219,234,254,0.6) 0%, transparent 70%), #ffffff"};
  @media (max-width: 768px) {
    padding: 64px 20px 56px;
  }
  @media (max-width: 480px) {
    padding: 48px 16px 48px;
  }
`;

const Inner = styled.div`
  text-align: center;
`;

const AnimatedBlock = styled.div<{ $delay?: number }>`
  ${(p) => fadeUpAnimation(p.$delay ?? 0)}
`;

const TopGroup = styled.div`
  margin-bottom: 24px;
`;

const Headline = styled.h1`
  font-size: clamp(1.9rem, 6vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#0F172A")};
  margin: 0 auto 20px;
  padding: 0 8px;
  max-width: 980px;
`;

const Subheadline = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  line-height: 1.6;
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
  max-width: 860px;
  margin: 0 auto 16px;
`;

const SupportText = styled.p`
  font-size: 1.125rem;
  color: ${(p) => (p.theme.isDarkMode ? "#60A5FA" : "#2563EB")};
`;

const CTAGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-bottom: 48px;
  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;
  padding: 14px 32px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  max-width: 320px;
  @media (min-width: 480px) {
    width: auto;
    min-width: 200px;
    font-size: 1.0625rem;
    padding: 16px 40px;
  }
  transition: background 0.2s, border-color 0.2s;
  background: ${(p) =>
    p.theme.isDarkMode ? "rgba(255,255,255,0.05)" : "#ffffff"};
  color: ${(p) => (p.theme.isDarkMode ? "#ffffff" : "#111827")};
  border: 2px solid
    ${(p) => (p.theme.isDarkMode ? "rgba(255,255,255,0.1)" : "#E5E7EB")};
  &:hover {
    background: ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.1)" : "#F9FAFB"};
    border-color: ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.2)" : "#D1D5DB"};
  }
`;

const VideoPlaceholder = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const VideoBox = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"};
  box-shadow: 0 8px 40px
      ${(p) =>
        p.theme.isDarkMode ? "rgba(0,0,0,0.5)" : "rgba(37,99,235,0.10)"},
    0 2px 8px
      ${(p) => (p.theme.isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.06)")};
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
